import { useEffect, useState } from 'react'
import { AlertTriangle, TrendingUp, TrendingDown, Landmark } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtEuro, fmtDate } from '../utils/fmt'

export default function Dashboard() {
  const { condominioAtivo } = useAppStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get(`/relatorios/${condominioAtivo.id}/dashboard`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false))
  }, [condominioAtivo])

  if (!condominioAtivo) return null

  const fracoes = data?.fracoes || []
  const topDevedores = data?.topDevedores || []
  const emAtraso = fracoes.filter(f => f.estado === 'EM_ATRASO').length
  const regularizados = fracoes.filter(f => f.estado === 'REGULARIZADO').length

  return (
    <>
      <Topbar title="Dashboard" />
      <div className="page-content">
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <>
            {/* Métricas */}
            <div className="metrics-grid">
              <div className="metric-card metric-red">
                <div className="metric-label"><AlertTriangle size={12} /> Quotas em dívida</div>
                <div className="metric-value">{fmtEuro(data?.totalDivida)}</div>
                <div className="metric-sub">{emAtraso} de {fracoes.length} frações em atraso</div>
              </div>
              <div className="metric-card metric-green">
                <div className="metric-label"><TrendingUp size={12} /> Receitas (mês)</div>
                <div className="metric-value">{fmtEuro(data?.receitasMes)}</div>
                <div className="metric-sub">{regularizados} frações regularizadas</div>
              </div>
              <div className="metric-card metric-amber">
                <div className="metric-label"><TrendingDown size={12} /> Despesas (mês)</div>
                <div className="metric-value">{fmtEuro(data?.despesasMes)}</div>
                <div className="metric-sub">Mês atual</div>
              </div>
              <div className="metric-card">
                <div className="metric-label"><Landmark size={12} /> Fundo de reserva</div>
                <div className="metric-value">{fmtEuro(data?.fundoReserva)}</div>
                <div className="metric-sub">Saldo acumulado</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, marginBottom: 14 }}>
              {/* Tabela CC */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Resumo contas correntes</span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Dívida = meses sem quota lançada</span>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr><th>Fração</th><th>Condómino</th><th>Quota/mês</th><th>Meses dívida</th><th>Total dívida</th><th>Estado</th></tr>
                    </thead>
                    <tbody>
                      {fracoes.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>
                          Sem frações. Cria frações em Configuração → Frações.
                        </td></tr>
                      ) : fracoes.map(f => (
                        <tr key={f.fracaoId}>
                          <td><strong>{f.designacao}</strong></td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.condominos?.[0] || '—'}</td>
                          <td style={{ fontSize: 12 }}>{fmtEuro(f.valorQuota)}</td>
                          <td style={{ fontWeight: f.mesesEmDivida > 0 ? 600 : 400, color: f.mesesEmDivida > 0 ? 'var(--red-dark)' : 'var(--text-secondary)' }}>
                            {f.mesesEmDivida > 0 ? f.mesesEmDivida : '—'}
                          </td>
                          <td style={{ fontWeight: 600, color: f.totalDivida > 0 ? 'var(--red-dark)' : 'var(--green-dark)' }}>
                            {f.totalDivida > 0 ? fmtEuro(f.totalDivida) : '—'}
                          </td>
                          <td>
                            <span className={`badge badge-${f.estado === 'REGULARIZADO' ? 'green' : 'red'}`}>
                              {f.estado === 'REGULARIZADO' ? 'OK' : 'Em atraso'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top devedores */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Maiores dívidas</span>
                </div>
                {topDevedores.length === 0 ? (
                  <div className="empty-state">
                    <p style={{ color: 'var(--green-dark)', fontSize: 14 }}>🎉 Todas as quotas em dia!</p>
                  </div>
                ) : topDevedores.map(f => (
                  <div key={f.fracaoId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--red-dark)', flexShrink: 0 }}>
                      {f.designacao}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{f.condominos?.[0] || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.mesesEmDivida} meses em falta</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red-dark)' }}>{fmtEuro(f.totalDivida)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
